import { Spinner } from '@/components/common'
import { ArrowBack, SearchIcon } from '@/components/icons'
import { BASE_APP_URL, SCROLLING_HEIGHT_ABS_ERROR, SCROLLING_THROTTLING_DURATION } from '@/constants/Common'
import { useDebounce } from '@/hooks/useDebounce'
import { useGetClaimUsers } from '@/services'
import { Player } from '@/types'
import { throttle } from 'lodash'
import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import ExternalScreen from './ExternalScreen'
import { AccountSearchInput } from './components'
import Players from './components/Players'

const LIMIT = 10
const OFFSET = 0

export const ClaimYourAccount = () => {
  const { partnerId } = useParams()
  const navigate = useNavigate()

  const [players, setPlayers] = useState<Map<number, Player>>(new Map())
  const [searchText, setSearchText] = useState<string>('')
  const [offset, setOffset] = useState<number>(OFFSET)

  const searchTextDebounce = useDebounce(searchText || '', 200)

  const {
    data: claimUserData,
    isLoading: isClaimUserDataLoading,
    isFetching,
  } = useGetClaimUsers({
    limit: LIMIT,
    offset: offset,
    query: searchTextDebounce,
  })

  useEffect(() => {
    if (isFetching) {
      if (offset === OFFSET) {
        setPlayers(new Map())
      }
    }
  }, [isFetching]) //eslint-disable-line

  const playerContentRef = useRef<HTMLDivElement>(null)

  const goBack = () => {
    navigate(-1)
  }

  const handleScroll = throttle(() => {
    if (
      (playerContentRef.current &&
        Math.abs(
          playerContentRef.current?.scrollTop +
            playerContentRef.current?.clientHeight -
            playerContentRef.current?.scrollHeight
        ) > SCROLLING_HEIGHT_ABS_ERROR) ||
      !claimUserData?.hasMore
    ) {
      return
    }

    setOffset((pre) => pre + LIMIT)
  }, SCROLLING_THROTTLING_DURATION)

  useEffect(() => {
    setOffset(OFFSET)
  }, [searchTextDebounce])

  useEffect(() => {
    if (claimUserData) {
      setPlayers((old) => {
        let temp: Map<number, Player>
        if (offset === OFFSET) {
          temp = new Map()
        } else {
          temp = old
        }

        claimUserData.hits.forEach((player) => {
          temp.set(player.id, player)
        })

        return temp
      })
    }
  }, [isFetching]) //eslint-disable-line

  return (
    <ExternalScreen>
      <div className="w-full h-full bg-gradient-to-tr from-[#05155E] from-15% via-[#0163D0] to-[#4B97FE] flex flex-col gap-y-6 relative">
        <div className="flex flex-row items-center justify-between w-full text-white mt-6 px-4">
          <ArrowBack width={48} height={48} onClick={goBack} className="cursor-pointer" />
          <a
            href={`${BASE_APP_URL}/signup`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm font-normal hover:opacity-70"
          >
            I don't have an account
          </a>
        </div>
        <div className="text-white px-4">
          <p className="text-3xl font-bold mb-2">Claim Your Account</p>
          <p className="text-sm font-normal opacity-70">
            Already Played in a tournament or a league? You may already have a player profile and a DUPR rating
          </p>
        </div>
        <div className="px-4">
          <AccountSearchInput
            placeholder="Search"
            value={searchText}
            onChange={(e) => {
              setSearchText(e.currentTarget.value)
            }}
          />
        </div>
        <div className="rounded-t-3xl h-[459px] bg-white relative overflow-hidden">
          {searchTextDebounce === '' ? (
            <div className="w-full h-full flex flex-col items-center justify-center text-navy-900 text-opacity-50 gap-y-4 p-20">
              <SearchIcon width={'100px'} height={'100px'} className="opacity-60" />
              <p className="text-sm text-center">
                Type your name in the search bar to begin searching for your results
              </p>
            </div>
          ) : (
            <>
              <div className="px-2 pt-8 pb-2 h-[80%] relative">
                {isClaimUserDataLoading && (
                  <div className="absolute flex flex-col justify-center items-center w-full h-full">
                    <Spinner className="w-10 h-10 fill-blue-600 text-gray-200" />
                  </div>
                )}
                <Players
                  ref={playerContentRef}
                  players={Array.from(players.values())}
                  onScroll={handleScroll}
                  partnerId={partnerId || ''}
                />
              </div>
              <div className="absolute h-[20%] bottom-0 flex flex-row items-center justify-center w-full border-t-[0.5px] gap-x-4 border-navy-900 border-opacity-10">
                <p className="text-navy-900 text-sm font-normal">Don't see yourself here?</p>
                <a
                  href={`${BASE_APP_URL}/signup`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-5 py-[14px] bg-primary-500 text-white text-sm font-semibold rounded-full hover:bg-opacity-70"
                >
                  Create Account
                </a>
              </div>
            </>
          )}
        </div>
      </div>
    </ExternalScreen>
  )
}

export default ClaimYourAccount
